/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { useComputed } from 'src/core/hooks/useComputed';
import { Feature } from 'src/luis/modules/features/types/Feature';
import { useModels } from 'src/luis/modules/models/useModels';
import { ModelType } from 'src/luis/types/ModelType';

export type UseConversationFeatures = {
    features: Feature[];
    isLoaded: boolean;
    isLoading: boolean;
};

/**
 * A hook used to initialize the app features lists by doing the following.
 * 1) Fetches the current app phrase lists, intents and entities.
 * 2) Return the features and the loading state.
 */
export const useConversationFeatures = (): UseConversationFeatures => {
    const { entityStores: entities, intentStores: intents, phraseListStores, isLoaded, isLoading } = useModels();

    const features = useComputed(
        () => [
            ...entities.filter(e => e.typeId !== ModelType.PatternAny).map(e => <Feature>{ id: e.id, name: e.name, typeId: 'entity' }),
            ...phraseListStores.map(p => <Feature>{ id: p.id, name: p.name, typeId: 'feature' })
        ],
        [entities, intents, phraseListStores]
    );

    return {
        features,
        isLoaded,
        isLoading
    };
};
